/**
 * entities/lift/api/useLifts — Typed query hook for the lift list.
 *
 * Wraps GET /api/lifts/ with TanStack Query.
 * Returns all lifts linked to the current customer.
 * Query is disabled when the user is not authenticated.
 *
 * Requests page_size=200 (the backend's maximum page size) so accounts with
 * more than the default page size worth of lifts are not silently truncated —
 * the list has no server-side pagination UI, so LiftsTable expects the full
 * set in one response.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api';
import { useSessionStore } from '@/entities/session';
import type { Lift, PaginatedLiftList } from '../model/types';

export const LIFTS_QUERY_KEY = ['lifts'] as const;

/**
 * Returns all lifts belonging to the authenticated customer.
 * Query is enabled only when the user is authenticated.
 *
 * The API returns a PaginatedLiftList envelope ({ count, next, previous, results }).
 * This hook unwraps the envelope and returns only the `results` array so consumers
 * work with Lift[] directly.
 */
export function useLiftsQuery() {
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);

  return useQuery<Lift[], Error>({
    queryKey: LIFTS_QUERY_KEY,
    queryFn: async (): Promise<Lift[]> => {
      const result = await (
        apiClient.GET as (
          p: string,
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )('/api/lifts/?page_size=200');

      if (result.error !== undefined) {
        const err = result.error;
        const detail =
          typeof err === 'object' && err !== null && 'detail' in err
            ? String((err as Record<string, unknown>)['detail'])
            : `API error: ${result.response.status}`;
        throw new Error(detail);
      }

      // Unwrap the paginated envelope — the API returns { count, next, previous, results }.
      const paginated = result.data as PaginatedLiftList;
      return paginated?.results ?? [];
    },
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
}
